/**
 * The only colours components reference.
 *
 * Light and dark are designed as separate themes. Light: white chrome and cards
 * over a warm off-white canvas. Dark: a near-black canvas with cards lifted onto
 * distinctly lighter layers — depth from surface steps, not from shadow.
 */
const pair = (base, dark) => ({ value: { base, _dark: dark } })

export const semanticTokens = {
  colors: {
    bg: {
      /** Application background. */
      canvas: pair('{colors.stone.50}', '{colors.stone.950}'),
      /** Chrome shares the canvas value in dark mode; borders separate it. */
      sidebar: pair('{colors.stone.0}', '{colors.stone.950}'),
      header: pair('{colors.stone.0}', '{colors.stone.950}'),

      /** Cards. White on light, a clear step above the canvas on dark. */
      surface: pair('{colors.stone.0}', '{colors.stone.900}'),
      surfaceSubtle: pair('{colors.stone.100}', '{colors.stone.925}'),
      surfaceRaised: pair('{colors.stone.0}', '{colors.stone.875}'),
      surfaceSunken: pair('{colors.stone.150}', '{colors.stone.975}'),

      /** Controls before interaction. */
      interactive: pair('{colors.stone.0}', '{colors.stone.875}'),
      input: pair('{colors.stone.0}', '{colors.stone.900}'),

      hover: pair('{colors.stone.100}', '{colors.stone.850}'),
      /** Pressed. Distinct from `selected`, which persists. */
      active: pair('{colors.stone.150}', '{colors.stone.800}'),
      /** Orange-tinted, so selection reads as brand rather than as grey. */
      selected: pair('{colors.orange.50}', '{colors.orange.950}'),
      disabled: pair('{colors.stone.100}', '{colors.stone.875}'),
      inverted: pair('{colors.stone.1000}', '{colors.stone.50}'),
      overlay: pair('{colors.scrim.light}', '{colors.scrim.dark}'),
    },

    fg: {
      default: pair('{colors.stone.1000}', '{colors.stone.25}'),
      /** Headings and metric values. */
      strong: pair('{colors.stone.1050}', '{colors.stone.0}'),
      muted: pair('{colors.stone.600}', '{colors.stone.400}'),
      // Dark needs a lighter step: stone.500 on a dark card is only 3.7:1, and
      // this role carries caption text.
      subtle: pair('{colors.stone.500}', '{colors.stone.450}'),
      disabled: pair('{colors.stone.400}', '{colors.stone.700}'),
      /** On a brand or status fill. */
      onSolid: pair('{colors.stone.0}', '{colors.stone.0}'),
      inverted: pair('{colors.stone.50}', '{colors.stone.1000}'),
    },

    icon: {
      default: pair('{colors.stone.600}', '{colors.stone.400}'),
      muted: pair('{colors.stone.400}', '{colors.stone.500}'),
      strong: pair('{colors.stone.800}', '{colors.stone.100}'),
    },

    border: {
      subtle: pair('{colors.stone.150}', '{colors.stone.850}'),
      default: pair('{colors.stone.200}', '{colors.stone.800}'),
      strong: pair('{colors.stone.300}', '{colors.stone.700}'),
      /** Focused or selected control edge. */
      focus: pair('{colors.orange.600}', '{colors.orange.400}'),
    },

    /**
     * Amber carries dark text in both modes — 6.6:1, and the same pairing as the
     * public site. `fg` is the ochre used for brand-coloured text and icons.
     */
    brand: {
      solid: pair('{colors.orange.400}', '{colors.orange.400}'),
      hover: pair('{colors.orange.500}', '{colors.orange.300}'),
      active: pair('{colors.orange.600}', '{colors.orange.200}'),
      subtle: pair('{colors.orange.50}', '{colors.orange.950}'),
      muted: pair('{colors.orange.100}', '{colors.orange.900}'),
      border: pair('{colors.orange.200}', '{colors.orange.800}'),
      contrast: pair('{colors.stone.1050}', '{colors.stone.950}'),
      fg: pair('{colors.orange.700}', '{colors.orange.300}'),
    },

    /** Supporting logo colours, used only where they carry meaning. */
    accent: {
      coral: pair('{colors.coral.600}', '{colors.coral.400}'),
      coralSubtle: pair('{colors.coral.50}', '{colors.coral.950}'),
      coralBorder: pair('{colors.coral.200}', '{colors.coral.800}'),
      teal: pair('{colors.teal.600}', '{colors.teal.400}'),
      tealSubtle: pair('{colors.teal.50}', '{colors.teal.950}'),
      tealBorder: pair('{colors.teal.200}', '{colors.teal.800}'),
      blue: pair('{colors.blue.600}', '{colors.blue.400}'),
      blueSubtle: pair('{colors.blue.50}', '{colors.blue.950}'),
      blueBorder: pair('{colors.blue.200}', '{colors.blue.800}'),
    },

    /**
     * Teal succeeds, orange waits, coral fails, blue informs. `*Fg` is the text
     * colour for the matching `*Subtle` background.
     */
    status: {
      success: pair('{colors.teal.600}', '{colors.teal.400}'),
      successSubtle: pair('{colors.teal.50}', '{colors.teal.950}'),
      successBorder: pair('{colors.teal.200}', '{colors.teal.800}'),
      successFg: pair('{colors.teal.700}', '{colors.teal.300}'),

      warning: pair('{colors.orange.500}', '{colors.orange.400}'),
      warningSubtle: pair('{colors.orange.50}', '{colors.orange.950}'),
      warningBorder: pair('{colors.orange.200}', '{colors.orange.800}'),
      warningFg: pair('{colors.orange.700}', '{colors.orange.300}'),

      error: pair('{colors.coral.600}', '{colors.coral.500}'),
      errorSubtle: pair('{colors.coral.50}', '{colors.coral.950}'),
      errorBorder: pair('{colors.coral.200}', '{colors.coral.800}'),
      errorFg: pair('{colors.coral.700}', '{colors.coral.300}'),

      info: pair('{colors.blue.600}', '{colors.blue.400}'),
      infoSubtle: pair('{colors.blue.50}', '{colors.blue.950}'),
      infoBorder: pair('{colors.blue.200}', '{colors.blue.800}'),
      infoFg: pair('{colors.blue.700}', '{colors.blue.300}'),

      neutral: pair('{colors.stone.500}', '{colors.stone.400}'),
      neutralSubtle: pair('{colors.stone.100}', '{colors.stone.875}'),
      neutralBorder: pair('{colors.stone.200}', '{colors.stone.700}'),
      neutralFg: pair('{colors.stone.600}', '{colors.stone.300}'),
    },

    focus: {
      ring: pair('{colors.orange.600}', '{colors.orange.400}'),
    },

    /**
     * Orange leads, teal supports, blue is tertiary, coral flags problems.
     *
     * The four categorical slots are assigned in this fixed order and never
     * cycled — a fifth series folds into "other", faceted small multiples, or a
     * second chart. Each mode's steps are chosen for that mode's surface rather
     * than flipped: both sets are validated for the lightness band, the chroma
     * floor, colour-vision separation and contrast (see the dataviz validator).
     * Changing a step means re-running it.
     *
     * Slots 5 and 6 are chart-only hues (violet, magenta) — six identities is
     * what a grouped chart of one row per teammate needs, and the brand four run
     * out at four. The whole 1–6 sequence is validated in this order.
     */
    chart: {
      1: pair('{colors.orange.600}', '{colors.orange.500}'),
      2: pair('{colors.teal.400}', '{colors.teal.400}'),
      3: pair('{colors.blue.500}', '{colors.blue.400}'),
      4: pair('{colors.coral.500}', '{colors.coral.500}'),
      5: pair('{colors.violet.500}', '{colors.violet.400}'),
      6: pair('{colors.magenta.500}', '{colors.magenta.400}'),
      grid: pair('{colors.stone.200}', '{colors.stone.850}'),
      axis: pair('{colors.stone.500}', '{colors.stone.500}'),
    },
  },

  shadows: {
    card: pair('{shadows.cardLight}', '{shadows.cardDark}'),
    floating: pair('{shadows.floatingLight}', '{shadows.floatingDark}'),
    dialog: pair('{shadows.dialogLight}', '{shadows.dialogDark}'),
    sticky: pair('{shadows.stickyLight}', '{shadows.stickyDark}'),
  },
}
